import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { TimeInterval } from '@cotera/api';
import { Button } from '../../components/ui/button';
import { Icon } from '@cotera/client/app/components';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { atcQueryKeys } from './query-keys';

interface SagaWorkflowsTableProps {
  timeInterval: TimeInterval;
  sagaTypeFilter?: { include: string } | { exclude: string[] };
}

function getSagaStatusLabel(
  status: string,
  completionResult: string | null
): { label: string; color: string } {
  if (status !== 'completed') {
    return { label: status, color: 'text-yellow-600' };
  }
  if (completionResult === 'success') {
    return { label: 'success', color: 'text-green-600' };
  }
  if (completionResult === 'failure') {
    return { label: 'failure', color: 'text-red-600' };
  }
  return { label: completionResult ?? 'completed', color: 'text-gray-600' };
}

interface EnrichedSagaRow {
  id: string;
  status: string;
  targetServiceKey: string;
  createdAt: string;
  completionResult: string | null;
  completionFailure: string | null;
  orgId: string | null;
  sagaId: string | null;
  sagaType: string | null;
  entityName: string | null;
  organizationName: string;
}

interface OrgGroup {
  orgId: string;
  organizationName: string;
  successCount: number;
  failureCount: number;
  otherCount: number;
  total: number;
  invocations: EnrichedSagaRow[];
}

export const SagaWorkflowsTable: React.FC<SagaWorkflowsTableProps> = ({
  timeInterval,
  sagaTypeFilter,
}) => {
  const client = useUntenantedClient();
  const queryClient = useQueryClient();
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [retryResult, setRetryResult] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retriedIds, setRetriedIds] = useState<Set<string>>(new Set());
  const [retryMetadata, setRetryMetadata] = useState<
    Map<string, { sagaType: string; entityName: string | null }>
  >(new Map());

  // Push the type filter to the server so the row cap applies to the right
  // rows (a rare type would otherwise be starved by a pre-filter limit). Mirror
  // the prop into the shape the query key / typed client expect.
  const serverFilter = useMemo(():
    | { include?: string; exclude?: string[] }
    | undefined => {
    if (sagaTypeFilter === undefined) {
      return undefined;
    }
    return 'include' in sagaTypeFilter
      ? { include: sagaTypeFilter.include }
      : { exclude: sagaTypeFilter.exclude };
  }, [sagaTypeFilter]);

  const {
    data: sagaData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getSagaInvocations({
        timeInterval,
        includeSagaType: serverFilter?.include,
        excludeSagaTypes: serverFilter?.exclude,
      });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.sagaInvocations(timeInterval, serverFilter),
  });

  const { data: restateUrlData } = useQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getRestateUrl({});
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.restateUrl(),
  });

  const orgIds = useMemo(
    () => [
      ...new Set(
        (sagaData ?? [])
          .map((row) => row.orgId)
          .filter((id): id is string => id !== null && id !== 'system')
      ),
    ],
    [sagaData]
  );

  const { data: orgs } = useQuery({
    queryFn: async () => {
      if (orgIds.length === 0) {
        return [];
      }
      const res = await client.atcVisibility.getOrgsByIds({ orgIds });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.organizationsByIds(orgIds),
    enabled: sagaData !== undefined && orgIds.length > 0,
  });

  const orgMap = useMemo(
    () => new Map(orgs?.map((org) => [org.id, org.name]) ?? []),
    [orgs]
  );

  const retryMutation = useMutation({
    mutationFn: async (invocationId: string) => {
      setRetryingId(invocationId);
      const res = await client.atcVisibility.retrySagaInvocation({
        invocationId,
      });
      return Assert.assertOk(res);
    },
    onSuccess: (data, invocationId) => {
      setRetryingId(null);
      setRetriedIds((prev) => new Set(prev).add(invocationId));
      setRetryMetadata((prev) => {
        const next = new Map(prev);
        next.set(data.newSagaId, {
          sagaType: data.sagaType,
          entityName: data.entityName,
        });
        return next;
      });
      setRetryResult({
        type: 'success',
        message: `New saga submitted: ${data.newSagaId}`,
      });
      void queryClient.invalidateQueries({
        queryKey: ['atc-visibility', 'saga-invocations'] as const,
      });
    },
    onError: (error: Error) => {
      setRetryingId(null);
      setRetryResult({
        type: 'error',
        message: error.message,
      });
    },
  });

  const enrichedData = useMemo<EnrichedSagaRow[]>(
    () =>
      (sagaData ?? []).map((row) => {
        const meta = retryMetadata.get(row.sagaId ?? '');
        return {
          ...row,
          sagaType: row.sagaType ?? meta?.sagaType ?? null,
          entityName: row.entityName ?? meta?.entityName ?? null,
          organizationName:
            row.orgId === 'system'
              ? 'System'
              : row.orgId !== null
              ? orgMap.get(row.orgId) ?? row.orgId
              : 'Unknown',
        };
      }),
    [sagaData, orgMap, retryMetadata]
  );

  const filteredData = useMemo(() => {
    if (sagaTypeFilter === undefined) {
      return enrichedData;
    }
    if ('include' in sagaTypeFilter) {
      return enrichedData.filter(
        (row) => row.sagaType === sagaTypeFilter.include
      );
    }
    return enrichedData.filter(
      (row) => !sagaTypeFilter.exclude.includes(row.sagaType ?? '')
    );
  }, [enrichedData, sagaTypeFilter]);

  const orgGroups = useMemo<OrgGroup[]>(() => {
    const groups = new Map<string, OrgGroup>();
    for (const row of filteredData) {
      const key = row.orgId ?? 'unknown';
      const existing = groups.get(key);
      if (existing === undefined) {
        groups.set(key, {
          orgId: key,
          organizationName: row.organizationName,
          successCount: 0,
          failureCount: 0,
          otherCount: 0,
          total: 0,
          invocations: [],
        });
      }
      const group = groups.get(key)!;
      group.total++;
      if (row.completionResult === 'success') {
        group.successCount++;
      } else if (row.completionResult === 'failure') {
        group.failureCount++;
      } else {
        group.otherCount++;
      }
      group.invocations.push(row);
    }
    return [...groups.values()].sort((a, b) => b.total - a.total);
  }, [filteredData]);

  const toggleOrg = (orgId: string) => {
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  };

  if (queryError !== null) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-sm text-red-800 font-medium">
          Failed to load saga invocations
        </p>
        <p className="text-sm text-red-600">
          {queryError instanceof Error ? queryError.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">
        Loading saga invocations...
      </p>
    );
  }

  if (filteredData.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No saga invocations found in this time range.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {retryResult !== null && (
        <div
          className={`p-3 rounded-md text-sm ${
            retryResult.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {retryResult.message}
        </div>
      )}
      <div className="bg-white rounded-lg shadow overflow-auto max-h-[600px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Organization</TableHead>
              <TableHead>Success</TableHead>
              <TableHead>Failures</TableHead>
              <TableHead>Other</TableHead>
              <TableHead>Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orgGroups.map((group) => (
              <React.Fragment key={group.orgId}>
                <TableRow
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleOrg(group.orgId)}
                >
                  <TableCell>
                    <Icon
                      icon={
                        expandedOrgs.has(group.orgId)
                          ? 'chevron-down'
                          : 'chevron-right'
                      }
                      className="w-4 h-4 text-gray-500"
                    />
                  </TableCell>
                  <TableCell className="font-medium text-gray-900">
                    {group.organizationName}
                  </TableCell>
                  <TableCell className="text-green-600 font-medium">
                    {group.successCount}
                  </TableCell>
                  <TableCell
                    className={
                      group.failureCount > 0 ? 'text-red-600 font-semibold' : ''
                    }
                  >
                    {group.failureCount}
                  </TableCell>
                  <TableCell
                    className={
                      group.otherCount > 0 ? 'text-yellow-600 font-medium' : ''
                    }
                  >
                    {group.otherCount}
                  </TableCell>
                  <TableCell className="font-medium">{group.total}</TableCell>
                </TableRow>
                {expandedOrgs.has(group.orgId) && (
                  <TableRow>
                    <TableCell colSpan={6} className="p-0">
                      <div className="bg-gray-50 border-y">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Actions</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Saga Type</TableHead>
                              <TableHead>Entity</TableHead>
                              <TableHead>Created</TableHead>
                              <TableHead>Failure</TableHead>
                              <TableHead>Invocation</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.invocations.map((inv) => {
                              const { label, color } = getSagaStatusLabel(
                                inv.status,
                                inv.completionResult
                              );
                              const isRetried = retriedIds.has(inv.id);
                              const isRetrying = retryingId === inv.id;
                              const shortId = `${inv.id.substring(0, 12)}...`;

                              return (
                                <TableRow key={inv.id}>
                                  <TableCell>
                                    {inv.completionResult !== 'success' && (
                                      <Button
                                        variant={
                                          isRetried ? 'ghost' : 'outline'
                                        }
                                        size="sm"
                                        onClick={() =>
                                          retryMutation.mutate(inv.id)
                                        }
                                        disabled={
                                          isRetried || retryMutation.isPending
                                        }
                                      >
                                        {isRetried
                                          ? 'Retried'
                                          : isRetrying
                                          ? 'Retrying...'
                                          : 'Retry'}
                                      </Button>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <span className={`font-medium ${color}`}>
                                      {label}
                                    </span>
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">
                                    {inv.sagaType ?? (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {inv.entityName ?? (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-xs text-gray-600">
                                    {inv.createdAt}
                                  </TableCell>
                                  <TableCell>
                                    {inv.completionFailure !== null ? (
                                      <span
                                        className="text-xs text-red-600"
                                        title={inv.completionFailure}
                                      >
                                        {inv.completionFailure.length > 80
                                          ? `${inv.completionFailure.substring(
                                              0,
                                              80
                                            )}...`
                                          : inv.completionFailure}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {restateUrlData?.restateUrl !==
                                    undefined ? (
                                      <a
                                        href={`${restateUrlData.restateUrl}/ui/invocations/${inv.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-mono text-xs text-blue-600 hover:underline"
                                      >
                                        {shortId}
                                      </a>
                                    ) : (
                                      <span className="font-mono text-xs text-gray-500">
                                        {shortId}
                                      </span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
